import { array, bool, func, object } from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import chunk from 'lodash/chunk';
import styled from 'styled-components';

import Typography from 'ravenjs/lib/Typography';
import { isEmpty } from 'ravenjs/utils/lodash';

import { DEFAULT_COLORS } from 'constants/colors';
import { EVENTS } from 'constants/events';
import { TOPIC_STATE_ROUTE_MAP } from 'constants/navigation';
import { actions as pendoActions } from 'modules/pendo';
import { convertWagesAndHoursToWageAndHour } from 'utils/filters';
import { getTopicNavigationToProp } from 'utils/navigation';

const AppSubNavigationTopicsStyledCommon = styled.div`
    display: flex;
    flex-direction: column;
`;

const AppSubNavigationTopicsStyledV1 = styled(AppSubNavigationTopicsStyledCommon)``;

const AppSubNavigationTopicsStyledV2 = styled(AppSubNavigationTopicsStyledCommon)`
    width: 294px;
    min-width: 240px;
    flex: 0 0 294px;
`;

const NavLinkStyledCommon = styled(NavLink)`
    color: ${DEFAULT_COLORS.BLACK};
`;

const NavLinkStyledV1 = styled(NavLinkStyledCommon)`
    :hover {
        color: ${DEFAULT_COLORS.LINK};
        text-decoration: none;
    }
`;

const NavLinkStyledV2 = styled(NavLinkStyledCommon)`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 4px;

    border-radius: 4px;
    padding: 8px 12px;
    box-sizing: border-box;
    &:hover {
        background: ${DEFAULT_COLORS.BLUE_7};
        color: ${DEFAULT_COLORS.BLACK};
        text-decoration: none;
    }
`;

const TopicRow = styled.div`
    display: flex;
    flex-direction: row;
`;

const TopicColCommon = styled.div`
    display: flex;
    flex-direction: column;
`;

const TopicColV1 = styled(TopicColCommon)`
    padding: 0 15px 0 0;
`;

const TopicColV2 = styled(TopicColCommon)``;

const linkStyle = {
    fontWeight: 'normal',
    fontFamily: 'Roboto',
    fontSize: '1rem',
    marginBlockStart: '.1875rem',
    marginBlockEnd: '.1875rem',
    marginInlineStart: '0',
    marginInlineEnd: '0',
};

const linkStyleV2 = {
    fontWeight: 'normal',
    fontFamily: 'Roboto',
    fontSize: '1rem',
};

const TopicsHeaderV2 = styled.div`
    padding: 12px;
    margin: 0;
`;

class AppSubNavigationTopics extends Component {
    static propTypes = {
        items: array,
        nav: object,
        sendPendoEvent: func.isRequired,
        isNavigationV2Enabled: bool,
    };

    static defaultProps = {
        items: [],
        nav: {},
        isNavigationV2Enabled: false,
    };

    onClickNav = (mainState, item) => {
        const { sendPendoEvent } = this.props;
        const pendoEvent = EVENTS.NAVIGATION.SELECT;

        pendoEvent.eventProperties.selection = `${mainState}:${item.state}`;
        sendPendoEvent(pendoEvent);
    };

    renderItems = (items, state) => {
        const { isNavigationV2Enabled } = this.props;

        if (isNavigationV2Enabled) {
            return (
                <TopicColV2>
                    {items.map(item => (
                        <NavLinkStyledV2
                            key={item.label}
                            to={getTopicNavigationToProp(state, item)}
                            style={linkStyleV2}
                            state={item.state}
                            onClick={() => this.onClickNav(state, item)}
                            // Added data attribute for keyboard navigation
                            data-submenu-item="true"
                            // Added tabIndex for keyboard focus
                            tabIndex={0}
                        >
                            {item.label}
                        </NavLinkStyledV2>
                    ))}
                </TopicColV2>
            );
        }

        const colSize = items.length % 2 === 0 ? items.length / 2 : 4;
        const chunks = chunk(items, colSize);
        if (chunks.length <= 1) {
            return (
                chunks.length === 1 && (
                    <TopicColV1>
                        {chunks[0].map(item => {
                            return (
                                <NavLinkStyledV1
                                    key={item.label}
                                    to={getTopicNavigationToProp(state, item)}
                                    style={linkStyle}
                                    state={item.state}
                                    onClick={() => this.onClickNav(state, item)}
                                >
                                    {item.label}
                                </NavLinkStyledV1>
                            );
                        })}
                    </TopicColV1>
                )
            );
        }

        return (
            <TopicRow>
                <TopicColV1>
                    {chunks[0].map(item => (
                        <NavLinkStyledV1
                            key={item.label}
                            to={getTopicNavigationToProp(state, item)}
                            style={linkStyle}
                            activeStyle={linkStyle}
                            state={item.state}
                            onClick={() => this.onClickNav(state, item)}
                        >
                            {item.label}
                        </NavLinkStyledV1>
                    ))}
                </TopicColV1>
                <TopicColV1>
                    {chunks[1].map(item => (
                        <NavLinkStyledV1
                            key={item.label}
                            to={getTopicNavigationToProp(state, item)}
                            style={linkStyle}
                            activeStyle={linkStyle}
                            state={item.state}
                            onClick={() => this.onClickNav(state, item)}
                        >
                            {item.label}
                        </NavLinkStyledV1>
                    ))}
                </TopicColV1>
            </TopicRow>
        );
    };

    render() {
        const { items, nav, isNavigationV2Enabled } = this.props;
        const { state } = nav;
        const topicName = !isEmpty(TOPIC_STATE_ROUTE_MAP[state])
            ? TOPIC_STATE_ROUTE_MAP[state]
            : 'Topics';

        convertWagesAndHoursToWageAndHour(items, 'label');

        const Container = isNavigationV2Enabled
            ? AppSubNavigationTopicsStyledV2
            : AppSubNavigationTopicsStyledV1;

        return (
            !isEmpty(items) &&
            items.length > 0 && (
                <Container>
                    {isNavigationV2Enabled ? (
                        <TopicsHeaderV2>
                            <Typography
                                fontFamily="Favorit"
                                fontWeight="400"
                                fontSize="18px"
                                fontStyle="normal"
                                style={{
                                    color: DEFAULT_COLORS.GREY_27,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0px',
                                    margin: 0,
                                }}
                            >
                                {topicName}
                            </Typography>
                        </TopicsHeaderV2>
                    ) : (
                        <Typography fontFamily="Roboto" fontWeight="bold" fontSize="1rem">
                            {topicName}
                        </Typography>
                    )}
                    {isNavigationV2Enabled ? (
                        this.renderItems(items, state)
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'row' }}>
                            {this.renderItems(items, state)}
                        </div>
                    )}
                </Container>
            )
        );
    }
}

const mapDispatchToProps = {
    sendPendoEvent: pendoActions.sendPendoEvent,
};

export { AppSubNavigationTopics as AppSubNavigationTopicsUnwrapped };
export default connect(null, mapDispatchToProps)(AppSubNavigationTopics);
